import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  Signal,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { SelectListOption, SuggestionItem } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, filter, switchMap } from 'rxjs';

import { TolerantPostViewModelMapper } from '../../../clients/tolerant-post/mapper/view-model.mapper';
import { SuggestedAddress, SuggestionItemWithAddress } from '../../../clients/tolerant-post/model/view.model';
import { TolerantPostService } from '../../../clients/tolerant-post/tolerant-post.service';
import { AddressField } from '../../../customer/model/address-field.model';
import { CustomerAddress } from '../../../customer/model/customer-information.model';
import { AddressViewService, SuggestionOfAddressField } from '../../../customer/services/address.view-service';
import { CustomerValidationService } from '../../../customer/services/customer-validation.service';
import { CustomerService } from '../../../customer/services/customer.service';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { useI18n } from '../../../i18n/services/i18n.service';
import { ControlItem, SmartAddressComponent } from './smart-address.component';

const GENERATE_SUGGESTIONS_DELAY_SECONDS = 1;

@Component({
  selector: 'cdf-smart-address-container',
  imports: [SmartAddressComponent],
  template: `
    <cdf-smart-address
      [(address)]="address"
      [(selectedTab)]="selectedTab"
      [(singleLineSearch)]="singleLineSearch"
      [(singleLineSearchCountry)]="singleLineSearchCountry"
      [singleLineSuggestions]="singleLineSuggestionItems()"
      [suggestions]="suggestions()"
      [isPrimaryContactData]="isPrimaryContactData()"
      [overwriteButtonTooltipText]="i18n('customerInformationDialog.info.selectAddress')"
      [isSingleLineSuggestionSelected]="isSingleLineSuggestionSelected()"
      [overwriteButtonText]="getOverwriteButtonText()"
      [availableCountries]="customerService.countries()"
      [availableProvinces]="availableProvinces()"
      [invalidAddressFields]="invalidAddressFields()"
      [disabled]="disabled()"
      [addressUiFormat]="addressUiFormat()"
      (selectSuggestion)="selectSuggestion($event)"
      (applyAddressSuggestion)="applySingleLineAddressSuggestion()"
      (selectedSuggestionForAddressField)="setCityForPostalCode($event)"
      (skipAddressValidation)="skipAddressValidation.emit()"
    />
  `,
  styleUrl: './smart-address.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SmartAddressContainer {
  readonly i18n = useI18n();

  readonly changeDetector = inject(ChangeDetectorRef);
  readonly logger = inject(NGXLogger);
  readonly addressViewService = inject(AddressViewService);
  readonly tolerantPostMapper = inject(TolerantPostViewModelMapper);
  readonly tolerantService = inject(TolerantPostService);
  readonly customerValidation = inject(CustomerValidationService);
  readonly customerService = inject(CustomerService);
  readonly featureVisibilityService = inject(FeatureVisibilityService);

  readonly address = model<CustomerAddress>();
  readonly isPrimaryContactData = input(false, { transform: booleanAttribute });
  readonly invalidAddressFields = input<AddressField[]>([]);
  readonly disabled = input<boolean>(false);
  readonly skipAddressValidation = output<void>();
  readonly availableProvinces = computed<SelectListOption[]>(() => {
    const provinceShown = this.addressUiFormat().some((row) => row.includes(AddressField.PROVINCE));
    return provinceShown ? this.addressViewService.getProvinceOptions(this.address()?.country) : [];
  });

  // Used for determining if changes were made to the address
  readonly addressHistory = signal<CustomerAddress>({});
  readonly selectedTab = signal<string>(ControlItem.ADDRESS);

  // Properties used for the single line search
  readonly singleLineSearch = signal('');
  readonly singleLineSearchCountry = signal<string | undefined>(undefined);
  readonly selectedSingleLineSuggestion = signal<SuggestedAddress | undefined>(undefined);

  private readonly singleSearchQuery = computed(() => {
    const search = this.singleLineSearch();
    const country = this.singleLineSearchCountry();
    if (search && country) {
      return { search, country };
    } else {
      return undefined;
    }
  });

  readonly singleLineSuggestions = toSignal(
    toObservable(this.singleSearchQuery).pipe(
      filter(Boolean),
      distinctUntilChanged(),
      debounceTime(GENERATE_SUGGESTIONS_DELAY_SECONDS * 1000),
      switchMap(async ({ search, country }) => this.tolerantService.resolveAddressFromSingleLineQuery(search, country)),
      catchError((e) => {
        this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', e);
        return EMPTY;
      }),
    ),
    {
      initialValue: [],
    },
  );

  readonly suggestions = toSignal(
    toObservable(this.address).pipe(
      filter(Boolean),
      distinctUntilChanged(),
      filter<CustomerAddress>((address) => this.customerValidation.isRelevantAddressInformationSet(address)),
      debounceTime(GENERATE_SUGGESTIONS_DELAY_SECONDS * 1000),
      switchMap(async (address) => this.fetchSuggestions(address)),
      catchError((e) => {
        this.logger.warn('#CDFTF0022 Failed fetching address suggestions using tolerant post.', e);
        return EMPTY;
      }),
    ),
    {
      initialValue: [],
    },
  );

  // Determine if a address suggestion was selected and not further changed in the one-line-search
  readonly isSingleLineSuggestionSelected = computed(
    () =>
      !!this.selectedSingleLineSuggestion() &&
      this.addressViewService.getAsSingleLineStringWithOptionalFields(
        this.tolerantPostMapper.mapToCustomerAddress(this.selectedSingleLineSuggestion()),
      ) === this.singleLineSearch(),
  );

  readonly validationSkipped = input<boolean>(false);

  readonly addressUiFormat = computed<AddressField[][]>(() => {
    const market = this.address()?.country ?? '';
    const format = this.addressViewService.getEditCustomerUIFormatForMarket(market);
    const isPrimary = this.isPrimaryContactData();
    return format.filter(
      (row) =>
        !(isPrimary && row.includes(AddressField.POST_BOX)) &&
        !row.some(
          (field) =>
            (field !== AddressField.PROVINCE && this.featureVisibilityService.isHidden(field)) ||
            (field === AddressField.PROVINCE && !this.validationSkipped()),
        ),
    );
  });

  readonly isRelevantAddressInformationPresent = computed(() => {
    const address = this.address();
    return address && Object.entries(address).some(([key, value]) => key !== 'country' && key !== 'province' && value);
  });

  readonly getOverwriteButtonText = computed(() =>
    this.isRelevantAddressInformationPresent()
      ? this.i18n('customerInformationDialog.button.overwriteAddress')
      : this.i18n('customerInformationDialog.button.setAddress'),
  );

  readonly singleLineSuggestionItems: Signal<SuggestionItemWithAddress[]> = computed(() => {
    const suggestedAddresses = this.singleLineSuggestions();
    if (!suggestedAddresses) {
      return [];
    }

    // Each single line suggestion should be unique regarding its string representation
    const uniqueAddresses = suggestedAddresses.reduce((acc, address) => {
      const label = this.addressViewService.getAsSingleLineStringWithOptionalFields(
        this.tolerantPostMapper.mapToCustomerAddress(address),
      );

      acc.set(label, {
        label,
        data: { address },
      });
      return acc;
    }, new Map<string, SuggestionItemWithAddress>());

    return Array.from(uniqueAddresses.values());
  });

  setCityForPostalCode(suggestionSelectedForField: SuggestionOfAddressField) {
    // Only set city if postal code suggestion was selected
    if (suggestionSelectedForField.addressField === AddressField.POSTAL_CODE) {
      const suggestionsWithPostalCode = this.suggestions()?.filter(
        (sugg) => sugg.postalCode === suggestionSelectedForField.suggestion.label,
      );

      // Update city if there's only one suggestion for the postal code
      if (
        suggestionsWithPostalCode &&
        suggestionsWithPostalCode.length > 0 &&
        suggestionsWithPostalCode.every((sugg) => suggestionsWithPostalCode[0].city === sugg.city)
      ) {
        this.address.update((addr) => ({
          ...addr,
          postalCode: suggestionsWithPostalCode[0].postalCode,
          city: suggestionsWithPostalCode[0].city,
        }));
        this.changeDetector.detectChanges(); // Required for the UI to detect changes
      }
    }
  }

  applySingleLineAddressSuggestion() {
    if (!this.selectedSingleLineSuggestion()) {
      this.logger.warn('#CDFTF0031 No suggested was selected. No suggestion can be applied.');
      return;
    }

    this.address.set(this.tolerantPostMapper.mapToCustomerAddress(this.selectedSingleLineSuggestion()));

    this.selectedTab.set(ControlItem.ADDRESS);
  }

  selectSuggestion(suggestion: SuggestionItem) {
    if (!('address' in suggestion.data)) {
      throw new Error('No address information was found in selected suggestion.');
    }
    this.selectedSingleLineSuggestion.set((suggestion as SuggestionItemWithAddress).data.address);
  }

  // Refetch suggestions if none match the currently set postal code
  async fetchSuggestions(address: CustomerAddress) {
    let suggestions = await this.tolerantService.resolveAddressFromCustomerAddress(address);

    // If there are not suggestions containing the postal code of the address - refetch without the city
    const postalCode = address.postalCode;
    if (postalCode && !suggestions.find((sugg) => sugg.postalCode?.includes(postalCode))) {
      const { city, ...addressWOCity } = address;
      suggestions = await this.tolerantService.resolveAddressFromCustomerAddress(addressWOCity);
    }
    return suggestions;
  }
}
